/**
 * Armar el home de la tienda, viéndolo.
 *
 * QUE PREGUNTA CONTESTA
 * ---------------------
 * "Que ve el cliente al abrir el link, sin buscar nada." Se lee de arriba abajo
 * en el mismo orden que el home: el encabezado, las ofertas y la lista de
 * secciones. Cada seccion se despliega ahi mismo para elegir sus productos:
 * antes aparecia dos veces, una en la lista para ordenarla y otra entera mas
 * abajo para llenarla.
 *
 * CONTROLES AFUERA, VISTA PREVIA ADENTRO
 * --------------------------------------
 * Cada bloque es una tarjeta del sistema —titulo, contador, botones— con un
 * recuadro blanco adentro que muestra lo que ve el cliente, con las piezas de
 * la tienda (`tienda/piezas.tsx`). La tienda es siempre negro sobre blanco; los
 * controles siguen el tema del sistema. Mezclarlos dejaba botones oscuros sobre
 * blanco en modo oscuro.
 *
 * SE TOCA Y SE GUARDA
 * -------------------
 * Tocar una tarjeta la mete o la saca del home en el momento. Se pinta antes de
 * que conteste el servidor (si falla, vuelve y avisa): esperar medio segundo a
 * que cada tarjeta cambie hace que se toque dos veces.
 *
 * Aca solo aparece lo publicado. Lo que no esta publicado no se puede poner en
 * el home; eso se decide en la otra pestaña.
 */
import { useMemo, useState, type ReactNode } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  closestCenter,
  DndContext,
  KeyboardSensor,
  MouseSensor,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  rectSortingStrategy,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { ChevronDown, ChevronRight, Plus } from "lucide-react";

import { clavesTienda, useCatalogo } from "@/components/tienda/consultas";
import {
  candidatosADestacar,
  clavePropia,
  conLasQueNoSeVen,
  esPropia,
  nombreEnTienda,
  ordenDeSecciones,
  resumenDeSeccion,
  seccionesDelHome,
  seccionVisible,
  tarjetasDeSeccion,
  tituloDeSeccion,
  type SeccionDelHome,
  type TarjetaDelHome,
} from "@/components/tienda/home";
import {
  nombreLegible,
  type OfertaPublica,
  type ProductoPublico,
  type SeccionConfigurada,
} from "@/components/tienda/logica";
import {
  BotonVerMas,
  OfertaEnVidriera,
  TarjetaDestacada,
  TarjetaElegible,
  Vidriera,
} from "@/components/tienda/vidriera";
import { Aviso } from "@/components/ui/aviso";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Cargando } from "@/components/ui/cargando";
import { Input } from "@/components/ui/input";
import { TituloSeccion } from "@/components/ui/titulo-seccion";
import { mostrarError, mostrarExito } from "@/lib/errores";
import { useActualizarConfig, useConfig, useProductos } from "@/lib/queries";
import { aNumero, type CambiosConfig, type Oferta, type Producto } from "@/lib/tipos";

import {
  useActualizarOferta,
  useAgregarALaTienda,
  useBorrarOferta,
  useCrearOferta,
  useBorrarSeccion,
  useCrearSeccion,
  useGuardarSeccion,
  useRenombrarSeccion,
  useSeccionesTienda,
  type SeccionTienda,
  useMarcarHome,
  useOfertas,
} from "./consultas";
import { DialogDestacados } from "./DialogDestacados";
import { DialogNombre, type Renombrando } from "./DialogNombre";
import { DialogOferta } from "./DialogOferta";
import { FilaOferta } from "./FilaOferta";
import { FilaSeccion } from "./FilaSeccion";

/** Mas que esto, la seccion se muestra recortada: los accesorios son cien. */
const TARJETAS_A_LA_VISTA = 12;

const SIN_TOCAR: ReadonlyMap<string, boolean> = new Map();

export interface PestanaHomeProps {
  onIrAPublicar: () => void;
}

export function PestanaHome({ onIrAPublicar }: PestanaHomeProps): JSX.Element {
  const catalogo = useCatalogo();
  const config = useConfig();
  const guardarConfig = useActualizarConfig();
  const qc = useQueryClient();
  const seccionesTienda = useSeccionesTienda();
  const crearSeccion = useCrearSeccion();
  const renombrarSeccion = useRenombrarSeccion();
  const borrarSeccion = useBorrarSeccion();

  const productos = useMemo(() => catalogo.data ?? [], [catalogo.data]);
  const propias = useMemo(() => seccionesTienda.data ?? [], [seccionesTienda.data]);
  // Un producto de una seccion propia sale de su seccion automatica en el home,
  // asi que aca tampoco se muestra ahi: tildado en una seccion donde el cliente
  // no lo ve, confundiria.
  const secciones = useMemo(() => {
    const ids = new Set(propias.flatMap((s) => s.productos.map((x) => x.producto_id)));
    return seccionesDelHome(productos)
      .map((s) => ({ ...s, tarjetas: s.tarjetas.filter((t) => !t.ids.some((id) => ids.has(id))) }))
      .filter((s) => s.tarjetas.length > 0);
  }, [productos, propias]);
  const candidatos = useMemo(() => candidatosADestacar(productos), [productos]);

  // Lo que el cliente NO ve, del catalogo entero: sin publicar, o publicado en
  // un rubro oculto. Es lo que se ofrece para agregar. Los usados en cero no:
  // la tienda no los muestra aunque se publiquen.
  const sistema = useProductos({ limite: 500 });
  const noPublicados = useMemo(() => {
    const visibles = new Set(productos.map((p) => p.id));
    return (sistema.data?.filas ?? []).filter(
      (p) => p.activo && !visibles.has(p.id) && !(p.es_usado && aNumero(p.stock_actual) <= 0),
    );
  }, [sistema.data, productos]);
  const agregar = useAgregarALaTienda();
  const [agregandoATienda, setAgregandoATienda] = useState(false);
  const agregandoId = agregar.isPending ? (agregar.variables?.ids[0] ?? null) : null;

  async function agregarALaTienda(p: Producto, seccion?: SeccionTienda): Promise<void> {
    try {
      await agregar.mutateAsync({ ids: [p.id], seccion_id: seccion?.id ?? null });
      mostrarExito(
        seccion ? `Agregado a ${seccion.titulo}` : "Agregado a la tienda",
        `${nombreLegible(p.nombre)} ya se ve en el home.`,
      );
    } catch (error) {
      mostrarError(error);
    }
  }

  // Que secciones estan desplegadas. Arrancan cerradas: la lista entra en una
  // pantalla y se abre la que se quiere tocar.
  const [abiertas, setAbiertas] = useState<ReadonlySet<string>>(() => new Set());
  function abrir(clave: string, abierta: boolean): void {
    setAbiertas((a) => {
      const nuevo = new Set(a);
      if (abierta) nuevo.add(clave);
      else nuevo.delete(clave);
      return nuevo;
    });
  }

  const cfg = config.data?.config;
  const nombres = cfg?.nombres_tienda ?? null;
  const [renombrando, setRenombrando] = useState<Renombrando | null>(null);

  // El orden recien arrastrado se pinta antes de que conteste el servidor,
  // atado a la config sobre la que se arrastro (igual que las ofertas).
  const [ordenLocal, setOrdenLocal] = useState<{
    de: unknown;
    secciones: SeccionConfigurada[];
  } | null>(null);
  const seccionesCfg =
    ordenLocal && ordenLocal.de === cfg ? ordenLocal.secciones : (cfg?.secciones_home ?? null);

  const deSiempre = new Map<string, string>([
    ...propias.map((s) => [clavePropia(s.id), s.titulo] as [string, string]),
    ...secciones.map((s) => [s.clave, s.titulo] as [string, string]),
  ]);
  const orden = ordenDeSecciones([...deSiempre.keys()], seccionesCfg);
  // Una propia se llama como su seccion; una automatica, como su titulo propio
  // en la config o como su rubro.
  const tituloDe = (clave: string): string =>
    esPropia(clave)
      ? (deSiempre.get(clave) ?? clave)
      : tituloDeSeccion(clave, deSiempre.get(clave) ?? clave, seccionesCfg);

  async function guardar(cambios: CambiosConfig): Promise<void> {
    await guardarConfig.mutateAsync(cambios);
    // La tienda lee el orden y los nombres de la vista publica, no de la config.
    void qc.invalidateQueries({ queryKey: clavesTienda.negocio });
  }

  /** Guarda el orden entero con titulos y visibilidad: la config es una sola lista. */
  async function guardarSecciones(
    claves: string[],
    cambio?: { clave: string } & Partial<Omit<SeccionConfigurada, "clave">>,
  ): Promise<void> {
    const nuevas = claves.map((clave) => {
      const antes = seccionesCfg?.find((c) => c.clave === clave);
      const propio = cambio?.clave === clave ? cambio : undefined;
      return {
        clave,
        titulo: propio && "titulo" in propio ? (propio.titulo ?? null) : (antes?.titulo ?? null),
        visible:
          propio && "visible" in propio ? propio.visible !== false : antes?.visible !== false,
      };
    });
    // Con las que hoy no se ven (un rubro oculto): sin esto se borraban de la
    // config y perdian su titulo y su lugar (`conLasQueNoSeVen`).
    const todas = conLasQueNoSeVen(nuevas, seccionesCfg);
    setOrdenLocal({ de: cfg, secciones: todas });
    try {
      await guardar({ secciones_home: todas });
    } catch (error) {
      setOrdenLocal(null);
      mostrarError(error);
    }
  }

  async function cambiarTitulo(clave: string, titulo: string): Promise<void> {
    if (esPropia(clave)) {
      const id = Number(clave.slice("propia:".length));
      const limpio = titulo.trim();
      if (limpio === "") return;
      try {
        await renombrarSeccion.mutateAsync({ id, titulo: limpio });
        void qc.invalidateQueries({ queryKey: clavesTienda.negocio });
      } catch (error) {
        mostrarError(error);
      }
      return;
    }
    await guardarSecciones(orden, { clave, titulo: titulo || null });
  }

  /** Una nueva va arriba de todo: es la que se esta por llenar. */
  async function nuevaSeccion(titulo: string): Promise<void> {
    try {
      const s = await crearSeccion.mutateAsync(titulo);
      await guardarSecciones([clavePropia(s.id), ...orden]);
      abrir(clavePropia(s.id), true);
      mostrarExito("Sección creada", "Ya está abierta: agregale productos.");
    } catch (error) {
      mostrarError(error);
    }
  }

  async function eliminarSeccion(clave: string): Promise<void> {
    const id = Number(clave.slice("propia:".length));
    const nombre = deSiempre.get(clave) ?? "esta sección";
    if (
      !window.confirm(
        `¿Borrar la sección «${nombre}»? Sus productos siguen publicados y vuelven a su sección de siempre.`,
      )
    )
      return;
    try {
      await borrarSeccion.mutateAsync(id);
      void qc.invalidateQueries({ queryKey: clavesTienda.negocio });
      mostrarExito("Sección borrada");
    } catch (error) {
      mostrarError(error);
    }
  }

  async function renombrar(nombre: string): Promise<void> {
    if (!renombrando) return;
    try {
      await guardar({ nombres_tienda: { ...(nombres ?? {}), [renombrando.clave]: nombre } });
      setRenombrando(null);
    } catch (error) {
      mostrarError(error);
    }
  }

  function abrirNombre(t: TarjetaDelHome): void {
    setRenombrando({ clave: t.clave, automatico: t.nombre, actual: nombres?.[t.clave] ?? null });
  }

  const hayTienda = secciones.length > 0 || propias.length > 0;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="max-w-2xl text-sm text-muted-foreground">
          Lo que ve el cliente al entrar, de arriba abajo. Lo que no está en el home igual se
          encuentra buscando o por categoría.
        </p>
        <Button type="button" onClick={() => setAgregandoATienda(true)}>
          <Plus aria-hidden="true" />
          Agregar productos a la tienda
        </Button>
      </div>

      <DialogDestacados
        abierto={agregandoATienda}
        modo="tienda"
        candidatos={[]}
        elegidas={new Set()}
        nombres={nombres}
        noPublicados={noPublicados}
        agregando={agregandoId}
        onCambiar={() => undefined}
        onAgregar={(p) => void agregarALaTienda(p)}
        onCerrar={() => setAgregandoATienda(false)}
      />

      <BloqueOfertas />

      {catalogo.isLoading ? <Cargando texto="Cargando la tienda…" /> : null}

      {catalogo.data && !hayTienda ? (
        <Aviso
          tono="info"
          accion={
            <Button type="button" variant="ghost" size="sm" onClick={onIrAPublicar}>
              Ir a publicar
            </Button>
          }
        >
          Todavía no hay nada publicado. Primero se publica, después se elige qué va al home.
        </Aviso>
      ) : null}

      {hayTienda ? (
        <BloqueOrden
          orden={orden}
          tituloDe={tituloDe}
          deSiempre={(clave) => deSiempre.get(clave) ?? clave}
          visibleDe={(clave) => seccionVisible(clave, seccionesCfg)}
          detalleDe={(clave) => {
            if (esPropia(clave)) {
              const p = propias.find((x) => clavePropia(x.id) === clave);
              const n = p ? tarjetasDeSeccion(productos, idsDe(p)).length : 0;
              return n === 0 ? "Vacía" : `${n} producto${n === 1 ? "" : "s"}`;
            }
            const s = secciones.find((x) => x.clave === clave);
            return s ? resumenDeSeccion(s) : "";
          }}
          creando={crearSeccion.isPending}
          onNueva={(t) => void nuevaSeccion(t)}
          onOrden={(claves) => void guardarSecciones(claves)}
          onTitulo={(clave, titulo) => void cambiarTitulo(clave, titulo)}
          onVisible={(clave, visible) => void guardarSecciones(orden, { clave, visible })}
          onBorrar={(clave) => void eliminarSeccion(clave)}
          abiertas={abiertas}
          onAbrir={abrir}
          contenidoDe={(clave) => {
            const apagada = !seccionVisible(clave, seccionesCfg);
            if (esPropia(clave)) {
              const p = propias.find((x) => clavePropia(x.id) === clave);
              return p ? (
                <ContenidoPropia
                  seccion={p}
                  apagada={apagada}
                  productos={productos}
                  candidatos={candidatos}
                  nombres={nombres}
                  noPublicados={noPublicados}
                  agregando={agregandoId}
                  onAgregar={(prod) => void agregarALaTienda(prod, p)}
                  onRenombrar={abrirNombre}
                />
              ) : null;
            }
            const s = secciones.find((x) => x.clave === clave);
            return s ? (
              <ContenidoSeccion
                seccion={s}
                apagada={apagada}
                nombres={nombres}
                onRenombrar={abrirNombre}
              />
            ) : null;
          }}
        />
      ) : null}

      <DialogNombre
        renombrando={renombrando}
        guardando={guardarConfig.isPending}
        onGuardar={(n) => void renombrar(n)}
        onCerrar={() => setRenombrando(null)}
      />
    </div>
  );
}

/** Los ids de una seccion propia, en su orden. */
function idsDe(s: SeccionTienda): number[] {
  return [...s.productos].sort((a, b) => a.lugar - b.lugar).map((x) => x.producto_id);
}

// ─────────────────────────────────────────────────────────────────────────────
// El orden de las secciones
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Las secciones del home en una lista corta que se arrastra. Arrastrar las
 * secciones enteras —con sus grillas— obligaba a mover bloques mas altos que
 * la pantalla; aca cada una es un renglon: el titulo se escribe encima, "Se
 * ve" la saca del home, las propias se borran desde el menu, y desplegada
 * muestra sus productos.
 */
function BloqueOrden({
  orden,
  tituloDe,
  deSiempre,
  visibleDe,
  detalleDe,
  creando,
  onNueva,
  onOrden,
  onTitulo,
  onVisible,
  onBorrar,
  abiertas,
  onAbrir,
  contenidoDe,
}: {
  orden: string[];
  tituloDe: (clave: string) => string;
  deSiempre: (clave: string) => string;
  visibleDe: (clave: string) => boolean;
  detalleDe: (clave: string) => string;
  creando: boolean;
  onNueva: (titulo: string) => void;
  onOrden: (claves: string[]) => void;
  onTitulo: (clave: string, titulo: string) => void;
  onVisible: (clave: string, visible: boolean) => void;
  onBorrar: (clave: string) => void;
  abiertas: ReadonlySet<string>;
  onAbrir: (clave: string, abierta: boolean) => void;
  contenidoDe: (clave: string) => ReactNode;
}): JSX.Element {
  const sensores = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );
  const [nueva, setNueva] = useState<string | null>(null);

  function alSoltar(e: DragEndEvent): void {
    const { active, over } = e;
    if (!over || active.id === over.id) return;
    const desde = orden.indexOf(String(active.id));
    const hasta = orden.indexOf(String(over.id));
    if (desde < 0 || hasta < 0) return;
    onOrden(arrayMove(orden, desde, hasta));
  }

  return (
    <Card padding="normal" className="space-y-3">
      <TituloSeccion
        descripcion="Abrí una para elegir sus productos. Arrastralas para ordenarlas y escribí sobre el título para cambiarlo."
        acciones={
          <Button type="button" variant="outline" size="sm" onClick={() => setNueva("")}>
            <Plus aria-hidden="true" />
            Nueva sección
          </Button>
        }
      >
        Secciones del home
      </TituloSeccion>

      {nueva !== null ? (
        <form
          className="flex flex-wrap items-center gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            if (nueva.trim() === "") return;
            onNueva(nueva.trim());
            setNueva(null);
          }}
        >
          <Input
            value={nueva}
            onChange={(e) => setNueva(e.target.value)}
            placeholder="Por ejemplo: Para regalar"
            maxLength={40}
            aria-label="Nombre de la sección nueva"
            className="min-w-0 flex-1"
            // Se abre para escribir el nombre: es lo unico que hay que hacer.
            // eslint-disable-next-line jsx-a11y/no-autofocus
            autoFocus
          />
          <Button type="submit" disabled={creando || nueva.trim() === ""}>
            Crear
          </Button>
          <Button type="button" variant="ghost" onClick={() => setNueva(null)}>
            Cancelar
          </Button>
        </form>
      ) : null}

      <DndContext sensors={sensores} collisionDetection={closestCenter} onDragEnd={alSoltar}>
        <SortableContext items={orden} strategy={verticalListSortingStrategy}>
          <ul className="rounded-md border border-border">
            {orden.map((clave) => (
              <FilaSeccion
                // Con el titulo en la clave, el campo vuelve a lo guardado
                // cuando cambia desde el servidor.
                key={`${clave}|${tituloDe(clave)}`}
                clave={clave}
                titulo={tituloDe(clave)}
                deSiempre={esPropia(clave) ? "" : deSiempre(clave)}
                detalle={detalleDe(clave)}
                propia={esPropia(clave)}
                visible={visibleDe(clave)}
                onTitulo={(t) => onTitulo(clave, t)}
                onVisible={(v) => onVisible(clave, v)}
                onBorrar={() => onBorrar(clave)}
                abierta={abiertas.has(clave)}
                onAbrir={(a) => onAbrir(clave, a)}
              >
                {abiertas.has(clave) ? contenidoDe(clave) : null}
              </FilaSeccion>
            ))}
          </ul>
        </SortableContext>
      </DndContext>
    </Card>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Una seccion propia
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Una seccion armada a mano: cualquier cosa publicada, de cualquier rubro, en
 * el orden que se quiera. Se guarda la lista entera cada vez.
 *
 * Los productos que la seccion tiene pero la tienda no muestra (se despublico
 * uno, se quedo sin precio) no se dibujan, pero se conservan al guardar: si no,
 * reordenar los borraria sin que nadie lo decida.
 */
function ContenidoPropia({
  seccion,
  apagada,
  productos,
  candidatos,
  nombres,
  noPublicados,
  agregando,
  onAgregar,
  onRenombrar,
}: {
  seccion: SeccionTienda;
  apagada: boolean;
  productos: ProductoPublico[];
  candidatos: TarjetaDelHome[];
  nombres: Record<string, string> | null;
  noPublicados: Producto[];
  agregando: number | null;
  onAgregar: (p: Producto) => void;
  onRenombrar: (t: TarjetaDelHome) => void;
}): JSX.Element {
  const guardar = useGuardarSeccion();
  const [abierto, setAbierto] = useState(false);

  const ids = useMemo(() => idsDe(seccion), [seccion]);
  const deServidor = useMemo(() => tarjetasDeSeccion(productos, ids), [productos, ids]);
  const [local, setLocal] = useState<{ de: TarjetaDelHome[]; lista: TarjetaDelHome[] } | null>(
    null,
  );
  const lista = local && local.de === deServidor ? local.lista : deServidor;
  const elegidas = useMemo(() => new Set(lista.map((t) => t.clave)), [lista]);

  const sensores = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 6 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 250, tolerance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  async function guardarLista(nueva: TarjetaDelHome[]): Promise<void> {
    setLocal({ de: deServidor, lista: nueva });
    const dibujados = new Set(deServidor.flatMap((t) => t.ids));
    const escondidos = ids.filter((id) => !dibujados.has(id)).map((id) => [id]);
    try {
      await guardar.mutateAsync({
        id: seccion.id,
        tarjetas: [...nueva.map((t) => t.ids), ...escondidos],
      });
    } catch (error) {
      setLocal(null);
      mostrarError(error);
    }
  }

  function alSoltar(e: DragEndEvent): void {
    const { active, over } = e;
    if (!over || active.id === over.id) return;
    const desde = lista.findIndex((t) => t.clave === active.id);
    const hasta = lista.findIndex((t) => t.clave === over.id);
    if (desde < 0 || hasta < 0) return;
    void guardarLista(arrayMove(lista, desde, hasta));
  }

  return (
    <div className="space-y-3">
      <BarraContenido
        texto={
          apagada
            ? OCULTA
            : lista.length === 0
              ? "Todavía está vacía. Agregale lo que quieras, de cualquier rubro."
              : "Arrastralos para ordenarlos. La × los saca de la sección."
        }
      >
        <Button type="button" variant="outline" size="sm" onClick={() => setAbierto(true)}>
          <Plus aria-hidden="true" />
          Agregar productos
        </Button>
      </BarraContenido>

      {lista.length > 0 ? (
        <Vidriera apagada={apagada}>
          <DndContext sensors={sensores} collisionDetection={closestCenter} onDragEnd={alSoltar}>
            <SortableContext items={lista.map((t) => t.clave)} strategy={rectSortingStrategy}>
              <div className="grid grid-cols-3 gap-x-2 gap-y-3 sm:grid-cols-4 lg:grid-cols-6">
                {lista.map((t, i) => (
                  <TarjetaDestacada
                    key={t.clave}
                    tarjeta={t}
                    nombre={nombreEnTienda(t, nombres)}
                    lugar={i + 1}
                    onQuitar={() => void guardarLista(lista.filter((x) => x.clave !== t.clave))}
                    onRenombrar={() => onRenombrar(t)}
                  />
                ))}
              </div>
            </SortableContext>
          </DndContext>
        </Vidriera>
      ) : null}

      <DialogDestacados
        abierto={abierto}
        modo="seccion"
        tituloSeccion={seccion.titulo}
        candidatos={candidatos}
        elegidas={elegidas}
        nombres={nombres}
        noPublicados={noPublicados}
        agregando={agregando}
        onAgregar={onAgregar}
        onCambiar={(t, sumar) =>
          void guardarLista(sumar ? [...lista, t] : lista.filter((x) => x.clave !== t.clave))
        }
        onCerrar={() => setAbierto(false)}
      />
    </div>
  );
}

/** Oculta en la lista: se dice en cada una, con lo que hay que hacer. */
const OCULTA = "Oculta: no va al home. Tildá «Se ve» para mostrarla.";

/** El renglon de arriba de una seccion abierta: que hacer, y los botones. */
function BarraContenido({ texto, children }: { texto: string; children: ReactNode }): JSX.Element {
  return (
    <div className="flex flex-wrap items-center justify-between gap-2">
      <p className="text-sm text-muted-foreground">{texto}</p>
      <div className="flex gap-1">{children}</div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Las ofertas
// ─────────────────────────────────────────────────────────────────────────────

/**
 * La oferta como la ve el cliente. La del sistema viene en su moneda; la
 * tienda la muestra en pesos. Con la cotizacion de hoy el numero es el mismo
 * que calcula la vista publica, salvo el redondeo.
 */
function aPublica(o: Oferta, cotizacion: number): OfertaPublica {
  const enPesos = (v: string | null): string | null => {
    if (v === null) return null;
    const n = Number(v) || 0;
    return String(o.moneda === "ARS" ? n : n * cotizacion);
  };
  return {
    id: o.id,
    clase: o.clase,
    titulo: o.titulo,
    detalle: o.detalle,
    precio_ars: enPesos(o.precio),
    precio_antes_ars: enPesos(o.precio_antes),
    codigo: null,
    porcentaje: o.porcentaje,
    imagen_url: o.imagen_url,
  };
}

function BloqueOfertas(): JSX.Element {
  const ofertas = useOfertas();
  // Para la foto del equipo de cada oferta, como en la tienda.
  const catalogo = useCatalogo();
  const config = useConfig();
  const actualizar = useActualizarOferta();
  const crear = useCrearOferta();
  const borrar = useBorrarOferta();
  const cotizacion = config.data?.cotizacion_vigente ?? 0;

  const delServidor = useMemo(() => ofertas.data ?? [], [ofertas.data]);
  // El orden recien arrastrado se pinta antes de que conteste el servidor, y
  // se guarda junto con la lista sobre la que se arrastro: cuando llega la
  // nueva, manda la del servidor.
  const [arrastrado, setArrastrado] = useState<{ de: Oferta[]; ids: number[] } | null>(null);
  const lista = useMemo(() => {
    if (!arrastrado || arrastrado.de !== delServidor) return delServidor;
    const porId = new Map(delServidor.map((o) => [o.id, o]));
    return arrastrado.ids.map((id) => porId.get(id)).filter((o): o is Oferta => Boolean(o));
  }, [arrastrado, delServidor]);
  const prendidas = lista.filter((o) => o.activa);
  const apagadas = lista.filter((o) => !o.activa);

  // Las guardadas arrancan plegadas si hay alguna prendida: lo que se mira
  // primero es lo que se ve. Sin ninguna prendida, se muestran: son las que hay
  // para elegir.
  const [verGuardadas, setVerGuardadas] = useState<boolean | null>(null);
  const guardadasAbiertas = verGuardadas ?? prendidas.length === 0;

  // `undefined` cerrado, `null` una nueva, una oferta para editarla.
  const [editando, setEditando] = useState<Oferta | null | undefined>(undefined);
  const ocupado = actualizar.isPending || crear.isPending || borrar.isPending;

  const sensores = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  /**
   * Guarda un orden entero, del 1 en adelante: primero las que se ven, en el
   * orden del carrusel, despues las apagadas. Las viejas pueden tener el orden
   * repetido, y renumerar todas es lo que hace que la pantalla y la tienda
   * digan lo mismo.
   */
  async function guardarOrden(nueva: Oferta[]): Promise<void> {
    setArrastrado({ de: delServidor, ids: nueva.map((o) => o.id) });
    try {
      await Promise.all(
        nueva.map((o, k) =>
          o.orden === k + 1
            ? null
            : actualizar.mutateAsync({ id: o.id, cambios: { orden: k + 1 } }),
        ),
      );
    } catch (error) {
      setArrastrado(null);
      mostrarError(error);
    }
  }

  /** Una que se prende va al final del carrusel: no desplaza a la que manda. */
  async function prender(o: Oferta, activa: boolean): Promise<void> {
    try {
      await actualizar.mutateAsync({ id: o.id, cambios: { activa } });
      const resto = prendidas.filter((x) => x.id !== o.id);
      const nuevaActivas = activa ? [...resto, { ...o, activa }] : resto;
      const nuevaApagadas = activa
        ? apagadas.filter((x) => x.id !== o.id)
        : [{ ...o, activa }, ...apagadas];
      await guardarOrden([...nuevaActivas, ...nuevaApagadas]);
    } catch (error) {
      mostrarError(error);
    }
  }

  function alSoltar(e: DragEndEvent): void {
    const { active, over } = e;
    if (!over || active.id === over.id) return;
    const desde = prendidas.findIndex((o) => o.id === active.id);
    const hasta = prendidas.findIndex((o) => o.id === over.id);
    if (desde < 0 || hasta < 0) return;
    void guardarOrden([...arrayMove(prendidas, desde, hasta), ...apagadas]);
  }

  /**
   * La copia va apagada y al lado de la original, y se abre para editarla: se
   * duplica para cambiarle algo, y dos ofertas iguales prendidas en la tienda
   * se leen como un error.
   */
  async function duplicar(o: Oferta): Promise<void> {
    try {
      const copia = await crear.mutateAsync({
        clase: o.clase,
        titulo: o.titulo,
        detalle: o.detalle,
        precio: o.precio === null ? null : Number(o.precio),
        moneda: o.moneda,
        precio_antes: o.precio_antes === null ? null : Number(o.precio_antes),
        producto_id: o.producto_id,
        porcentaje: o.porcentaje,
        activa: false,
        orden: lista.length + 1,
      });
      setVerGuardadas(true);
      setEditando(copia);
    } catch (error) {
      mostrarError(error);
    }
  }

  async function eliminar(o: Oferta): Promise<void> {
    if (!window.confirm(`¿Borrar la oferta «${o.titulo}»? No se puede deshacer.`)) return;
    try {
      await borrar.mutateAsync(o.id);
      mostrarExito("Oferta borrada");
    } catch (error) {
      mostrarError(error);
    }
  }

  const fila = (o: Oferta, ordenable: boolean) => (
    <FilaOferta
      key={o.id}
      oferta={o}
      ordenable={ordenable}
      ocupado={ocupado}
      onEditar={() => setEditando(o)}
      onDuplicar={() => void duplicar(o)}
      onBorrar={() => void eliminar(o)}
      onPrender={(activa) => void prender(o, activa)}
    />
  );

  return (
    <Card padding="normal" className="space-y-4">
      <TituloSeccion
        descripcion={
          lista.length === 0
            ? "Van arriba de todo en el home. Todavía no hay ninguna."
            : `${prendidas.length === 0 ? "Ninguna se ve" : prendidas.length === 1 ? "1 se ve" : `${prendidas.length} se ven`} en la tienda${apagadas.length === 0 ? "" : ` · ${apagadas.length} guardada${apagadas.length === 1 ? "" : "s"}`}`
        }
        acciones={
          <Button type="button" variant="outline" size="sm" onClick={() => setEditando(null)}>
            <Plus aria-hidden="true" />
            Nueva oferta
          </Button>
        }
      >
        Ofertas
      </TituloSeccion>

      {/* ── Como las ve el cliente: solo las prendidas, con la tarjeta real ── */}
      {prendidas.length > 0 ? (
        <Vidriera sinMargen>
          <div className="flex gap-3 overflow-x-auto p-4">
            {prendidas.map((o) => (
              <OfertaEnVidriera
                key={o.id}
                oferta={aPublica(o, cotizacion)}
                ancha={prendidas.length === 1}
                producto={catalogo.data?.find((p) => p.id === o.producto_id) ?? null}
                onEditar={() => setEditando(o)}
              />
            ))}
          </div>
        </Vidriera>
      ) : lista.length > 0 ? (
        <Aviso tono="info" compacto>
          Ninguna oferta se ve en la tienda. Tildá «Se ve» en las que quieras mostrar.
        </Aviso>
      ) : null}

      {/* ── La lista para administrarlas ── */}
      {prendidas.length > 0 ? (
        <section className="space-y-1">
          <TituloSeccion nivel="etiqueta" as="h3">
            Se ven, en este orden
          </TituloSeccion>
          <DndContext sensors={sensores} collisionDetection={closestCenter} onDragEnd={alSoltar}>
            <SortableContext
              items={prendidas.map((o) => o.id)}
              strategy={verticalListSortingStrategy}
            >
              <ul className="rounded-md border border-border">
                {prendidas.map((o) => fila(o, true))}
              </ul>
            </SortableContext>
          </DndContext>
        </section>
      ) : null}

      {apagadas.length > 0 ? (
        <section className="space-y-1">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            aria-expanded={guardadasAbiertas}
            onClick={() => setVerGuardadas(!guardadasAbiertas)}
            className="-ml-2"
          >
            {guardadasAbiertas ? (
              <ChevronDown aria-hidden="true" />
            ) : (
              <ChevronRight aria-hidden="true" />
            )}
            {guardadasAbiertas ? "Guardadas" : `Mostrar las ${apagadas.length} guardadas`}
          </Button>
          {guardadasAbiertas ? (
            <DndContext sensors={sensores}>
              <SortableContext items={[]}>
                <ul className="rounded-md border border-border">
                  {apagadas.map((o) => fila(o, false))}
                </ul>
              </SortableContext>
            </DndContext>
          ) : null}
        </section>
      ) : null}

      <DialogOferta
        abierto={editando !== undefined}
        oferta={editando ?? null}
        ordenNueva={lista.length + 1}
        onCerrar={() => setEditando(undefined)}
      />
    </Card>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Una seccion de la tienda
// ─────────────────────────────────────────────────────────────────────────────

function ContenidoSeccion({
  seccion,
  apagada,
  nombres,
  onRenombrar,
}: {
  seccion: SeccionDelHome;
  /** Apagada en Secciones del home: no va al home, aunque tenga tildadas. */
  apagada: boolean;
  nombres: Record<string, string> | null;
  onRenombrar: (t: TarjetaDelHome) => void;
}): JSX.Element {
  const marcar = useMarcarHome();

  // Lo tocado se pinta antes de que conteste el servidor. Se guarda junto con
  // la seccion sobre la que se toco: cuando llega la nueva, manda la del
  // servidor y lo tocado deja de contar solo.
  const [tocado, setTocado] = useState<{ de: SeccionDelHome; m: ReadonlyMap<string, boolean> }>(
    () => ({ de: seccion, m: new Map() }),
  );
  const tocadas = tocado.de === seccion ? tocado.m : SIN_TOCAR;
  function setTocadas(f: (m: ReadonlyMap<string, boolean>) => Map<string, boolean>): void {
    setTocado((t) => ({ de: seccion, m: f(t.de === seccion ? t.m : SIN_TOCAR) }));
  }

  const [todas, setTodas] = useState(false);

  const tarjetas = seccion.tarjetas.map((t) => ({
    ...t,
    enHome: tocadas.get(t.clave) ?? t.enHome,
  }));
  const recortada = !todas && tarjetas.length > TARJETAS_A_LA_VISTA;
  const aLaVista = recortada ? tarjetas.slice(0, TARJETAS_A_LA_VISTA) : tarjetas;
  const enHome = tarjetas.filter((t) => t.enHome).length;

  async function cambiar(lista: TarjetaDelHome[], en_home: boolean): Promise<void> {
    const ids = lista.flatMap((t) => t.ids);
    if (ids.length === 0) return;
    setTocadas((m) => {
      const nuevo = new Map(m);
      for (const t of lista) nuevo.set(t.clave, en_home);
      return nuevo;
    });
    try {
      await marcar.mutateAsync({ ids, en_home });
    } catch (error) {
      setTocadas((m) => {
        const nuevo = new Map(m);
        for (const t of lista) nuevo.delete(t.clave);
        return nuevo;
      });
      mostrarError(error);
    }
  }

  return (
    <div className="space-y-3">
      <BarraContenido texto={apagada ? OCULTA : "Tocá una para meterla o sacarla del home."}>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          disabled={enHome === tarjetas.length}
          onClick={() =>
            void cambiar(
              tarjetas.filter((t) => !t.enHome),
              true,
            )
          }
        >
          Poner todas
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          disabled={enHome === 0}
          onClick={() =>
            void cambiar(
              tarjetas.filter((t) => t.enHome),
              false,
            )
          }
        >
          Sacar todas
        </Button>
      </BarraContenido>

      <Vidriera apagada={apagada || enHome === 0}>
        <div className="grid grid-cols-3 gap-x-2 gap-y-3 sm:grid-cols-4 lg:grid-cols-6">
          {aLaVista.map((t) => (
            <TarjetaElegible
              key={t.clave}
              tarjeta={t}
              nombre={nombreEnTienda(t, nombres)}
              onCambiar={() => void cambiar([t], !t.enHome)}
              onRenombrar={() => onRenombrar(t)}
            />
          ))}
        </div>

        {tarjetas.length > TARJETAS_A_LA_VISTA ? (
          <BotonVerMas
            abierta={todas}
            total={tarjetas.length}
            onClick={() => setTodas((v) => !v)}
          />
        ) : null}
      </Vidriera>
    </div>
  );
}
